/**
 * Supabase AI Repository
 * Handles AI chat via Supabase Edge Functions with persistent conversations
 */

import type {
  IAiRepository,
  AiChatContext,
  AiResponse,
  ConversationMessage,
  ConversationSummary,
} from '../interfaces/IAiRepository'
import { CONVERSATION_MESSAGE_LIMIT } from '../interfaces/IAiRepository'
import { requireSupabaseClient } from '@/lib/supabase'

export class RateLimitError extends Error {
  retryAfter: number
  limit: number

  constructor(message: string, retryAfter: number, limit: number) {
    super(message)
    this.name = 'RateLimitError'
    this.retryAfter = retryAfter
    this.limit = limit
  }
}

export class SupabaseAiRepository implements IAiRepository {
  async getOrCreateConversation(
    insightId: string,
    userId: string
  ): Promise<string> {
    const supabase = requireSupabaseClient()
    const conversationType =
      !insightId || insightId === 'general' ? 'general' : 'insight'

    const { data: existing, error: findError } = await supabase
      .from('conversations')
      .select('id')
      .eq('user_id', userId)
      .eq('insight_id', insightId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()

    if (!findError && existing) return existing.id

    const { data: created, error: createError } = await supabase
      .from('conversations')
      .insert({
        user_id: userId,
        insight_id: insightId,
        conversation_type: conversationType,
      } as Record<string, unknown>)
      .select('id')
      .single()

    if (createError || !created) {
      throw new Error(
        `Failed to create conversation: ${createError?.message || 'No data returned'}`
      )
    }
    return created.id
  }

  async createNewConversation(
    insightId: string,
    userId: string,
    conversationType?: 'insight' | 'general' | 'sandbox'
  ): Promise<string> {
    const supabase = requireSupabaseClient()
    const resolvedType =
      conversationType ||
      (!insightId || insightId === 'general' ? 'general' : 'insight')

    const { data: created, error: createError } = await supabase
      .from('conversations')
      .insert({
        user_id: userId,
        insight_id: insightId,
        conversation_type: resolvedType,
      } as Record<string, unknown>)
      .select('id')
      .single()

    if (createError || !created) {
      throw new Error(
        `Failed to create conversation: ${createError?.message || 'No data returned'}`
      )
    }
    return created.id
  }

  async getConversationMessages(
    conversationId: string
  ): Promise<ConversationMessage[]> {
    const supabase = requireSupabaseClient()

    // Fetch all messages (up to limit * 2 for user+assistant pairs)
    const { data, error } = await supabase
      .from('messages')
      .select('id, role, content, created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .limit(CONVERSATION_MESSAGE_LIMIT * 2)

    if (error) throw new Error(`Failed to fetch messages: ${error.message}`)

    return (data || []).map((m) => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      createdAt: m.created_at,
    }))
  }

  async getConversationUserMessageCount(
    conversationId: string
  ): Promise<number> {
    const supabase = requireSupabaseClient()

    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', conversationId)
      .eq('role', 'user')

    if (error) return 0
    return count || 0
  }

  async saveMessage(
    conversationId: string,
    role: 'user' | 'assistant',
    content: string
  ): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error: msgError } = await supabase.from('messages').insert({
      conversation_id: conversationId,
      role,
      content,
    } as Record<string, unknown>)

    if (msgError) console.error('Failed to save message:', msgError.message)

    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId)
  }

  async listConversations(
    userId: string,
    conversationType?: 'regular' | 'sandbox'
  ): Promise<ConversationSummary[]> {
    const supabase = requireSupabaseClient()

    let query = supabase
      .from('conversations')
      .select('id, insight_id, conversation_type, created_at, updated_at')
      .eq('user_id', userId)

    if (conversationType === 'sandbox') {
      query = query.eq('conversation_type', 'sandbox')
    } else {
      // Default: exclude sandbox conversations
      query = query.neq('conversation_type', 'sandbox')
    }

    const { data: convos, error } = await query
      .order('updated_at', { ascending: false })
      .limit(50)

    if (error) throw new Error(`Failed to list conversations: ${error.message}`)
    if (!convos || convos.length === 0) return []

    // Parallelize all per-conversation queries instead of sequential N+1
    const summaryPromises = convos.map(async (c) => {
      // Run last-message and count queries in parallel
      const [lastMsgResult, countResult] = await Promise.all([
        supabase
          .from('messages')
          .select('content, role')
          .eq('conversation_id', c.id)
          .order('created_at', { ascending: false })
          .limit(1),
        supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('conversation_id', c.id),
      ])

      // Ticker lookup only when needed
      let ticker: string | undefined
      if (
        c.insight_id &&
        c.insight_id !== 'general' &&
        c.insight_id !== 'sandbox'
      ) {
        const { data: insight } = await supabase
          .from('insights')
          .select('ticker')
          .eq('id', c.insight_id)
          .single()
        ticker = insight?.ticker || undefined
      }

      return {
        id: c.id,
        insightId: c.insight_id || 'general',
        conversationType: c.conversation_type as
          | 'insight'
          | 'general'
          | 'sandbox',
        ticker,
        messageCount: countResult.count || 0,
        lastMessage:
          lastMsgResult.data?.[0]?.content?.substring(0, 80) || undefined,
        createdAt: c.created_at,
        updatedAt: c.updated_at,
      } satisfies ConversationSummary
    })

    return Promise.all(summaryPromises)
  }

  async deleteConversation(conversationId: string): Promise<void> {
    const supabase = requireSupabaseClient()

    // Delete messages first (in case no CASCADE), then conversation
    await supabase.from('messages').delete().eq('conversation_id', conversationId)

    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId)

    if (error) {
      throw new Error(`Failed to delete conversation: ${error.message}`)
    }
  }

  async deleteConversations(conversationIds: string[]): Promise<void> {
    if (conversationIds.length === 0) return
    const supabase = requireSupabaseClient()

    // Delete messages first, then conversations
    await supabase
      .from('messages')
      .delete()
      .in('conversation_id', conversationIds)

    const { error } = await supabase
      .from('conversations')
      .delete()
      .in('id', conversationIds)

    if (error) {
      throw new Error(`Failed to delete conversations: ${error.message}`)
    }
  }

  async sendMessage(
    insightId: string,
    message: string,
    context: AiChatContext,
    conversationId?: string,
    options?: { sandbox?: boolean }
  ): Promise<AiResponse> {
    const supabase = requireSupabaseClient()

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession()

    if (sessionError || !session) {
      throw new Error('Not authenticated. Please log in again.')
    }

    let history: { role: string; content: string }[] = []
    if (conversationId) {
      try {
        const msgs = await this.getConversationMessages(conversationId)
        history = msgs.map((m) => ({ role: m.role, content: m.content }))
      } catch (err) {
        console.error('Failed to fetch history, continuing without:', err)
      }
    }

    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        insightId,
        message,
        context: {
          ticker: context.ticker,
          sentiment: context.sentiment || 'neutral',
          content: context.content || '',
        },
        history,
        ...(options?.sandbox !== undefined && { sandbox: options.sandbox }),
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    })

    if (error) {
      if (
        error.message?.includes('Rate limit') ||
        data?.error === 'Rate limit exceeded'
      ) {
        throw new RateLimitError(
          data?.message || 'Too many messages. Please wait a moment.',
          data?.retryAfter || 60,
          data?.limit || 20
        )
      }
      throw new Error(`Failed to get AI response: ${error.message}`)
    }

    if (data?.error === 'Rate limit exceeded') {
      throw new RateLimitError(
        data.message || 'Too many messages. Please wait a moment.',
        data.retryAfter || 60,
        data.limit || 20
      )
    }

    return {
      message: data.message || 'Sorry, I could not generate a response.',
      timestamp: new Date(data.timestamp || Date.now()),
    }
  }
}
